/** CMS-02 — dashboard over `GET /v1/cms/stats/overview` (docs/06 §2.2). */
import type { AuditEntryDTO, CmsStatsOverviewResponse } from '@akpl/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { api } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/layout/AppShell';
import { auditTone, Badge, Panel, PanelHeader } from '../components/ui/panel';
import { useT } from '../i18n';
import { formatNumber, formatStamp } from '../lib/format';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Panel className="px-4 py-3.5">
      <p className="text-[12px] text-muted">{label}</p>
      <p className="mt-1 text-[28px] leading-8 font-bold text-ink">{value}</p>
    </Panel>
  );
}

function AuditLine({ entry, locale }: { entry: AuditEntryDTO; locale: string }) {
  return (
    <li className="border-b border-line px-4 py-3 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <Badge tone={auditTone(entry.action)}>{entry.action}</Badge>
        <span className="text-[12px] text-muted">{formatStamp(entry.createdAt, locale)}</span>
      </div>
      <p className="mt-1.5 text-[13px] text-ink">
        {entry.entityType}
        {entry.entityId ? ` · ${entry.entityId}` : ''}
      </p>
      <p className="text-[12px] text-muted">{entry.actorEmail ?? entry.actorUserId ?? '—'}</p>
    </li>
  );
}

export function Dashboard() {
  const { t, i18n } = useT();
  const { me } = useAuth();
  const locale = i18n.language;
  const query = useQuery<CmsStatsOverviewResponse>({
    queryKey: ['cms', 'stats', 'overview'],
    queryFn: () => api.cmsStatsOverview(),
  });

  const stats = query.data;

  return (
    <>
      <PageHeader
        title={t('cms:dashboard.greeting', {
          name: me?.displayName ?? me?.email ?? t('cms:dashboard.title'),
        })}
        subtitle={t('cms:dashboard.subtitle')}
      />

      <div className="px-6 pb-8">
        {query.isError && (
          <p role="alert" className="mb-4 text-[13px] text-danger">
            {t('common:states.error')}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat
            label={t('cms:dashboard.playedToday')}
            value={formatNumber(stats?.songsPlayed.today ?? 0, locale)}
          />
          <Stat
            label={t('cms:dashboard.played7d')}
            value={formatNumber(stats?.songsPlayed.d7 ?? 0, locale)}
          />
          <Stat
            label={t('cms:dashboard.played30d')}
            value={formatNumber(stats?.songsPlayed.d30 ?? 0, locale)}
          />
          <Stat
            label={t('cms:dashboard.activeSessions')}
            value={formatNumber(stats?.activeSessions ?? 0, locale)}
          />
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[1.4fr_1fr]">
          <Panel>
            <PanelHeader title={t('cms:dashboard.topSongs')} />
            {stats && stats.topSongs.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-muted">
                {t('common:states.empty')}
              </p>
            ) : (
              <ol>
                {(stats?.topSongs ?? []).map((song, index) => (
                  <li
                    key={song.songId}
                    className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-0"
                  >
                    <span className="w-5 text-[12px] text-muted">{index + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-ink">
                        {song.title}
                      </span>
                      <span className="block truncate text-[12px] text-muted">
                        {song.artistNames}
                      </span>
                    </span>
                    <span className="text-[13px] font-semibold text-ink">
                      {formatNumber(song.plays, locale)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Panel>

          <Panel>
            <PanelHeader
              title={t('cms:dashboard.recentAudit')}
              action={
                <Link to="/audit" className="text-[12px] font-semibold text-pink hover:underline">
                  {t('cms:audit.title')}
                </Link>
              }
            />
            {stats && stats.recentAudit.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-muted">
                {t('common:states.empty')}
              </p>
            ) : (
              <ul>
                {(stats?.recentAudit ?? []).map((entry) => (
                  <AuditLine key={entry.id} entry={entry} locale={locale} />
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
